import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { branding } from '@/config/branding';
import { Info, X } from 'lucide-react';
import packageJson from '../../package.json';

interface VersionInfoProps {
  className?: string;
}

export default function VersionInfo({ className = '' }: VersionInfoProps) {
  const { t } = useTranslation();
  const [, setForceUpdate] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(n => n + 1);
    };
    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // 从 package.json 获取版本号和其他信息
  const version = packageJson.version;
  const appName = packageJson.name;

  // 构建时间（从环境变量获取，需要在构建时注入）
  const buildTime = import.meta.env.VITE_BUILD_TIME || 'Unknown';

  return (
    <>
      {/* Version Icon Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors ${className}`}
        title={t('version.versionInfo')}
      >
        <Info size={20} />
      </button>

      {/* Version Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                {t('version.versionInfo')}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title={t('common.close')}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* App Name */}
              <div className="flex items-center gap-3">
                <img
                  src={branding.logo}
                  alt={branding.name}
                  className="w-10 h-10 rounded object-contain"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 capitalize">
                    {appName.replace(/-/g, ' ')}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t('version.aiPowered')}
                  </p>
                </div>
              </div>

              {/* Version Details */}
              <div className="space-y-3 pt-2">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">
                    {t('settings.version')}
                  </span>
                  <span className="text-sm font-mono font-semibold text-gray-800">
                    v{version}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">
                    {t('version.updateTime')}
                  </span>
                  <span className="text-xs font-mono text-gray-700">
                    {buildTime !== 'Unknown'
                      ? new Date(buildTime).toLocaleString('zh-CN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                © 2026 HopeAI Agents. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
